import { API_BASE_URL, REQUEST_TIMEOUT } from '../constants';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  token: string;
  admin: {
    id: number;
    email: string;
    name: string;
  };
}

interface ForgotPasswordRequest {
  email: string;
}

interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

interface ApiResponse<T = any> {
  message?: string;
  data?: T;
}

class AuthService {
  private baseUrl = API_BASE_URL;

  private async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  setToken(token: string): void {
    localStorage.setItem('admin_token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('admin_token');
  }

  removeToken(): void {
    localStorage.removeItem('admin_token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token || typeof token !== 'string') return false;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      const now = Date.now() / 1000;

      return payload.exp && payload.exp > now && payload.iat && payload.iat <= now;
    } catch {
      this.removeToken();
      return false;
    }
  }

  getCurrentAdmin(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.id,
        email: payload.email,
        name: payload.name,
        type: payload.type
      };
    } catch {
      return null;
    }
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    if (!credentials.email || !credentials.password) {
      throw new Error('Email e senha são obrigatórios');
    }

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Credenciais inválidas');
      }

      const data = await response.json();
      this.setToken(data.token);
      return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tempo limite esgotado. Tente novamente.');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      if (error.name === 'TypeError') {
        throw new Error('Servidor indisponível. Tente novamente em alguns minutos.');
      }
      throw error;
    }
  }

  logout(): void {
    this.removeToken();
    window.location.href = '/login';
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    if (!email || typeof email !== 'string') {
      throw new Error('Email é obrigatório');
    }

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/admin/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao enviar email de recuperação');
      }

      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tempo limite esgotado. Tente novamente.');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      if (error.name === 'TypeError') {
        throw new Error('Servidor indisponível. Tente novamente em alguns minutos.');
      }
      throw error;
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse> {
    if (!data.email || !data.code || !data.newPassword) {
      throw new Error('Todos os campos são obrigatórios');
    }

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao redefinir senha');
      }

      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tempo limite esgotado. Tente novamente.');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      if (error.name === 'TypeError') {
        throw new Error('Servidor indisponível. Tente novamente em alguns minutos.');
      }
      throw error;
    }
  }

  async getProfile(): Promise<any> {
    const token = this.getToken();
    if (!token) throw new Error('Sessão expirada. Faça login novamente.');

    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/admin/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Sessão expirada. Redirecionando para login...');
        }
        const error = await response.json();
        throw new Error(error.message || 'Erro ao buscar perfil');
      }

      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tempo limite esgotado. Tente novamente.');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      if (error.name === 'TypeError') {
        throw new Error('Servidor indisponível. Tente novamente em alguns minutos.');
      }
      throw error;
    }
  }

async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
  const token = this.getToken();
  if (!token) {
    this.logout();
    throw new Error('Sessão expirada. Redirecionando para login...');
  }

  if (!options.headers) options.headers = {};

  if (!(options.body instanceof FormData)) {
    (options.headers as any)['Content-Type'] = 'application/json';

  } else {

    delete (options.headers as any)['Content-Type'];
  }

  (options.headers as any)['Authorization'] = `Bearer ${token}`;

  try {
    const response = await this.fetchWithTimeout(`${this.baseUrl}${url}`, options);

    if (response.status === 401) {
      this.logout();
      throw new Error('Sessão expirada. Redirecionando para login...');
    }

    return response;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error('Tempo limite esgotado. Tente novamente.');
    }
    if (error.message === 'Failed to fetch') {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }
    if (error.name === 'TypeError') {
      throw new Error('Servidor indisponível. Tente novamente em alguns minutos.');
    }
    throw error;
  }
}
}

export const authService = new AuthService();
export type { LoginRequest, LoginResponse, ForgotPasswordRequest, ResetPasswordRequest, ApiResponse };
